import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import type { BackgroundColorMeta } from '@jira/admin-pages__system-announcement-banner/src/BackgroundColorMeta.types.tsx';
import Button from '@atlaskit/button';
import CloseIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { HEIGHT } from '../../../common/constants.tsx';
import { messages } from './messages.tsx';

type Props = {
	isDismissable?: boolean;
	children: ReactNode;
	onDismiss: () => void;
	backgroundTheme: BackgroundColorMeta;
};

export const Announcement = ({
	onDismiss,
	children,
	isDismissable = false,
	backgroundTheme,
}: Props) => {
	const isPageHydrating = useIsHydrateRenderMode();
	const { formatMessage } = useIntl();
	const { color: backgroundColor, textColor } = backgroundTheme;

	return (
		<Outer
			backgroundColor={backgroundColor}
			data-ssr-placeholder={__SERVER__ && !isPageHydrating ? 'announcement-banner' : undefined}
			data-ssr-placeholder-replace={
				!__SERVER__ && !isPageHydrating ? 'announcement-banner' : undefined
			}
		>
			<Inner textColor={textColor}>{children}</Inner>
			{isDismissable && (
				<ButtonWrapper>
					<Button
						appearance="subtle"
						spacing="compact"
						iconBefore={
							<CloseIcon size="small" color={textColor} label={formatMessage(messages.dismiss)} />
						}
						onClick={onDismiss}
					/>
				</ButtonWrapper>
			)}
		</Outer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Outer = styled.div<{ backgroundColor: string }>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${HEIGHT}px`,
	overflow: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) => props.backgroundColor,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Inner = styled.div<{ textColor: string }>({
	boxSizing: 'border-box',
	paddingTop: token('space.100'),
	paddingRight: token('space.600'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.600'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `${HEIGHT}px`,
	textAlign: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'p, ul, li, ol': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) => props.textColor,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a, a:hover, a:visited, a:focus, a:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) => props.textColor,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		textDecoration: 'underline',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	position: 'absolute',
	right: token('space.150'),
	top: token('space.400'),
	marginTop: token('space.negative.150'),
});
